import { Button, Group, Stack, Text } from '@mantine/core'
import { useEffect, useRef, useState } from 'react'

import {
  useRegisterWebPushDeviceMutation,
  useUpdateWebPushPreferenceMutation,
} from '../api/web-push.api'

import AppModal from '@v2/commons/components/AppModal/AppModal.component'

export const CONSENT_DISMISSAL_TTL_MS = 24 * 60 * 60 * 1000

interface NotificationConsentPromptProps {
  membershipId: string
  browserInstallationId: string
  permissionIssue?: 'denied' | 'default'
}

export const NotificationConsentPrompt = ({
  permissionIssue,
  ...context
}: NotificationConsentPromptProps) => {
  const storageKey = permissionIssue
    ? `notification-permission-warning-dismissed:${context.membershipId}:${context.browserInstallationId}:${permissionIssue}`
    : `notification-consent-dismissed:${context.membershipId}:${context.browserInstallationId}`
  const blocked = permissionIssue === 'denied'
  const permissionWarning = blocked
    ? 'Your saved preference was enabled, but this browser is blocking notifications. Allow notifications in your browser site settings, then open Notifications in the side menu to enable them for this membership.'
    : permissionIssue === 'default'
    ? 'Your notification preference is enabled, but browser permission is missing. Select Enable notifications and choose Allow in the browser prompt, or allow notifications in your browser site settings.'
    : null
  const [closed, setClosed] = useState(() => {
    try {
      const expiresAt = Number(localStorage.getItem(storageKey))
      return Number.isFinite(expiresAt) && expiresAt > Date.now()
    } catch {
      return false
    }
  })
  const [preparingRegistration, setPreparingRegistration] = useState(false)
  const [preparationError, setPreparationError] = useState(false)
  const isMount = useRef(true)
  const mutation = useRef<{ abort: () => void } | undefined>(undefined)
  const [registerDevice, registration] = useRegisterWebPushDeviceMutation()
  const [updatePreference, preference] = useUpdateWebPushPreferenceMutation()
  const busy =
    preparingRegistration || registration.isLoading || preference.isLoading
  const error = preparationError || registration.isError || preference.isError
  const outcome = registration.isSuccess
    ? 'enabled'
    : preference.isSuccess
    ? 'denied'
    : null

  useEffect(() => {
    isMount.current = true
    return () => {
      isMount.current = false
      mutation.current?.abort()
    }
  }, [context.membershipId])

  const dismiss = () => {
    if (busy) return
    try {
      if (!outcome) {
        localStorage.setItem(
          storageKey,
          String(Date.now() + CONSENT_DISMISSAL_TTL_MS),
        )
      }
    } catch {
      return
    }
    setClosed(true)
  }

  const enable = async () => {
    if (busy || blocked) return
    setPreparingRegistration(true)
    setPreparationError(false)
    registration.reset()
    preference.reset()
    let permission: NotificationPermission
    let token: string | undefined
    try {
      permission =
        Notification.permission === 'default'
          ? await Notification.requestPermission()
          : Notification.permission
      if (!isMount.current) return
      if (permission === 'default') {
        dismiss()
        return
      }
      if (permission === 'granted') {
        const { getWebPushToken } = await import('../utils/getWebPushToken')
        if (!isMount.current) return
        token = await getWebPushToken()
        if (!isMount.current) return
        if (Notification.permission !== 'granted') {
          throw new Error('Notification permission changed')
        }
      }
    } catch {
      if (isMount.current) setPreparationError(true)
      return
    } finally {
      if (isMount.current) setPreparingRegistration(false)
    }
    if (!isMount.current) return
    if (permission === 'denied') {
      mutation.current = updatePreference({
        ...context,
        notificationsEnabled: false,
      })
    } else if (token !== undefined) {
      mutation.current = registerDevice({
        ...context,
        token,
        notificationsEnabled: true,
      })
    }
  }

  return (
    <AppModal
      opened={!closed}
      onClose={dismiss}
      title={
        permissionIssue && outcome !== 'enabled'
          ? 'Browser permission required'
          : 'Browser notifications'
      }
      withCloseButton={false}
      closeOnClickOutside={!busy}
      closeOnEscape={!busy}
    >
      <Stack data-testid="notification-consent-prompt-modal" aria-busy={busy}>
        <Text
          data-testid="notification-consent-status-label"
          role={permissionIssue && !outcome ? 'alert' : 'status'}
        >
          {outcome === 'enabled'
            ? 'Notifications are enabled for this membership in this browser.'
            : outcome === 'denied'
            ? 'Notifications are disabled. To enable them later, allow notifications in your browser site settings.'
            : permissionWarning ??
              'Enable notifications for this membership in this browser? You may also be asked for browser permission.'}
        </Text>
        {error && (
          <Text
            c="red"
            role="alert"
            data-testid="notification-consent-error-label"
          >
            Could not save your notification choice. Please try again.
          </Text>
        )}
        <Group justify="flex-end">
          <Button
            variant="default"
            disabled={busy}
            onClick={dismiss}
            data-testid="notification-consent-dismiss-button"
          >
            {outcome || blocked ? 'Done' : 'Not now'}
          </Button>
          {!outcome && !blocked && (
            <Button
              loading={busy}
              disabled={busy}
              onClick={() => void enable()}
              data-testid="notification-consent-enable-button"
            >
              {error ? 'Try again' : 'Enable notifications'}
            </Button>
          )}
        </Group>
      </Stack>
    </AppModal>
  )
}
